/**
 * Numeric helpers for RFQ surfaces.
 *
 * DRF serialises DecimalFields as strings ("459500.000"), so any value that
 * looks numeric on the RFQ payloads may arrive as a string. Calling
 * `.toFixed()` / arithmetic on those crashed the supplier quote form. Every
 * RFQ component funnels API numerics through these helpers before
 * formatting or summing.
 */

/**
 * Coerce an API numeric (number | string | null | undefined) to a number.
 * Non-numeric input yields `fallback` (default 0).
 *
 * @param value - Raw value from the API or a form field
 * @param fallback - Value to return when input is null/blank/NaN
 * @returns A finite number
 */
export function toNumber(value: unknown, fallback = 0): number {
  if (value === null || value === undefined || value === '') return fallback;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Coerce an API numeric to a number, or `null` when absent / non-numeric.
 * Use for optional fields such as `target_unit_price` so a missing target
 * stays "no target" instead of becoming 0.
 *
 * @param value - Raw value from the API
 * @returns A finite number or null
 */
export function toNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}
